import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setClassificationValues } from "../../../../ducks/classification/classification";
import { getClassificationValues } from "../../../../ducks/classification/selectors";
import { recalcNumberClassificationValues } from "../../../../evergis/core/classification/valuesUpdate";
import { useClassificationQueryParams } from "../../../../hooks/location/useClassificationQueryParams";
import { useDialogReset } from "../../../../hooks/useDialogs";
import { useToggle } from "../../../../hooks/useToggle";

type ClassificationBoundaries = {
  to: number;
  from: number;
  min: number;
  max: number;
  isReversed: boolean;
};

type ClassificationBoundariesUpdate = {
  setTo: (v: number) => void;
  setFrom: (v: number) => void;
  updateTo: (v: number) => void;
  updateFrom: (v: number) => void;
  recalc: VoidFunction;
  reverse: VoidFunction;
};

export const useClassificationParameterBoundaries = (): [ClassificationBoundaries, ClassificationBoundariesUpdate] => {
  const dispatch = useDispatch();
  const resetDialog = useDialogReset();
  const values = useSelector(getClassificationValues);
  const { parameter } = useClassificationQueryParams();
  const [isReversed, toggleReversed] = useToggle();
  const isStrokeWidth = parameter === "strokeWidth" || parameter === "stroke.width";
  const min = isStrokeWidth ? 0.5 : 1;
  const max = isStrokeWidth ? 24 : 64;

  const valuesCount = values ? values.length : 0;
  const currentFrom = valuesCount ? (values[0].parameterValue as number) : min;
  const currentTo = valuesCount ? (values[valuesCount - 1].parameterValue as number) : max;

  const [from, setFrom] = useState(Math.max(currentFrom, min));
  const [to, setTo] = useState(Math.min(currentTo, max));

  const updateFrom = useCallback(
    (value: number) => {
      if (isReversed ? to < max : to > max) {
        setTo(max);
      }

      setFrom(Math.max(min, Math.min(value, to, max)));
    },
    [min, setFrom, from, to, isReversed],
  );

  const updateTo = useCallback(
    (value: number) => {
      if (isReversed ? from > min : from < min) {
        setFrom(min);
      }

      setTo(Math.min(max, Math.max(value, from, min)));
    },
    [max, setTo, to, isReversed],
  );

  const recalc = useCallback(() => {
    const valuesRecalculated = recalcNumberClassificationValues(values, from, to);

    dispatch(setClassificationValues(valuesRecalculated));
    resetDialog();
  }, [dispatch, values, parameter, from, to]);

  const reverse = useCallback(() => {
    setFrom(to);
    setTo(from);
    toggleReversed();
  }, [from, setFrom, setTo, to]);

  return [
    { to, from, min, max, isReversed },
    { setFrom, updateFrom, setTo, updateTo, recalc, reverse },
  ];
};
